import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiRequest } from '@/lib/queryClient';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ThumbsUp, ThumbsDown, MessageSquare } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

interface FeedbackRow {
  _id: string;
  messageId: string;
  conversationId: string;
  userEmail: string;
  rating: 'positive' | 'negative';
  category: string | null;
  comment: string | null;
  aiModel: string | null;
  createdAt: string;
  updatedAt: string;
}

interface CategoryBreakdown {
  _id: string;
  count: number;
}

interface TrendDay {
  _id: string;
  positive: number;
  negative: number;
}

interface FeedbackResponse {
  summary: { total: number; positive: number; negative: number };
  categoryBreakdown: CategoryBreakdown[];
  trend: TrendDay[];
  rows: FeedbackRow[];
  pagination: { total: number; page: number; limit: number; pages: number };
}

const CATEGORY_LABELS: Record<string, string> = {
  wrong_info: 'Wrong information',
  not_helpful: 'Not helpful',
  off_topic: 'Off topic',
  good_answer: 'Good answer',
  other: 'Other',
};

export default function AIChatFeedback() {
  const [ratingFilter, setRatingFilter] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate]     = useState('');
  const [page, setPage]           = useState(1);

  const params = new URLSearchParams({ page: String(page), limit: '50' });
  if (ratingFilter) params.set('rating', ratingFilter);
  if (startDate)    params.set('startDate', startDate);
  if (endDate)      params.set('endDate', endDate);

  const { data, isLoading } = useQuery({
    queryKey: ['/api/admin/chat/feedback', ratingFilter, startDate, endDate, page],
    queryFn: () => apiRequest('GET', `/api/admin/chat/feedback?${params}`) as unknown as Promise<FeedbackResponse>,
  });

  const summary  = data?.summary  || { total: 0, positive: 0, negative: 0 };
  const rows     = data?.rows     || [];
  const catBreak = data?.categoryBreakdown || [];
  const pagination = data?.pagination;

  const positiveRate = summary.total > 0
    ? Math.round((summary.positive / summary.total) * 100)
    : 0;

  function applyFilters() {
    setPage(1);
  }

  function clearFilters() {
    setRatingFilter('');
    setStartDate('');
    setEndDate('');
    setPage(1);
  }

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold">AI Chat Feedback</h1>

      {/* Summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-1">
              <MessageSquare className="h-4 w-4" /> Total Ratings
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{summary.total.toLocaleString()}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-1">
              <ThumbsUp className="h-4 w-4 text-green-600" /> Positive
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{summary.positive.toLocaleString()}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-1">
              <ThumbsDown className="h-4 w-4 text-red-500" /> Negative
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-500">{summary.negative.toLocaleString()}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Satisfaction Rate</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{positiveRate}%</div>
          </CardContent>
        </Card>
      </div>

      {/* Category breakdown */}
      {catBreak.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Negative Feedback — Categories</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-3">
              {catBreak.map(c => (
                <div key={c._id} className="flex items-center gap-2 border rounded-md px-3 py-2">
                  <span className="text-sm font-medium">{CATEGORY_LABELS[c._id] ?? c._id}</span>
                  <Badge variant="secondary">{c.count}</Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Filters</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-3 items-end">
            <div className="flex flex-col gap-1">
              <label className="text-xs text-muted-foreground">Rating</label>
              <Select value={ratingFilter} onValueChange={setRatingFilter}>
                <SelectTrigger className="w-36">
                  <SelectValue placeholder="All ratings" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">All ratings</SelectItem>
                  <SelectItem value="positive">Positive</SelectItem>
                  <SelectItem value="negative">Negative</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs text-muted-foreground">From</label>
              <Input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="w-40" />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs text-muted-foreground">To</label>
              <Input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="w-40" />
            </div>
            <Button onClick={applyFilters}>Apply</Button>
            <Button variant="outline" onClick={clearFilters}>Clear</Button>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            Feedback Entries {pagination ? `(${pagination.total} total)` : ''}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="text-sm text-muted-foreground py-4">Loading…</div>
          ) : rows.length === 0 ? (
            <div className="text-sm text-muted-foreground py-4">No feedback yet.</div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Rating</TableHead>
                      <TableHead>User</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead>Comment</TableHead>
                      <TableHead>Model</TableHead>
                      <TableHead>When</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rows.map(row => (
                      <TableRow key={row._id}>
                        <TableCell>
                          {row.rating === 'positive' ? (
                            <span className="flex items-center gap-1 text-green-600 text-sm">
                              <ThumbsUp className="h-3.5 w-3.5" /> Positive
                            </span>
                          ) : (
                            <span className="flex items-center gap-1 text-red-500 text-sm">
                              <ThumbsDown className="h-3.5 w-3.5" /> Negative
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="text-sm">{row.userEmail}</TableCell>
                        <TableCell className="text-sm">
                          {row.category ? (CATEGORY_LABELS[row.category] ?? row.category) : '—'}
                        </TableCell>
                        <TableCell className="text-sm max-w-xs truncate" title={row.comment ?? undefined}>
                          {row.comment || '—'}
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {row.aiModel ? row.aiModel.split('.').pop() ?? row.aiModel : '—'}
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                          {formatDistanceToNow(new Date(row.createdAt), { addSuffix: true })}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              {pagination && pagination.pages > 1 && (
                <div className="flex items-center justify-end gap-2 mt-4">
                  <Button
                    variant="outline" size="sm"
                    disabled={page <= 1}
                    onClick={() => setPage(p => p - 1)}
                  >
                    Previous
                  </Button>
                  <span className="text-sm text-muted-foreground">
                    Page {pagination.page} of {pagination.pages}
                  </span>
                  <Button
                    variant="outline" size="sm"
                    disabled={page >= pagination.pages}
                    onClick={() => setPage(p => p + 1)}
                  >
                    Next
                  </Button>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
